import { inject, observer } from 'mobx-react';
import React from 'react';
import {
  AnalyticsTrigger,
  Button,
  Card,
  CardContent,
  CardFooter,
  Carousel,
  CarouselItem,
  Container,
  EqualHeightProvider,
  Heading,
  Separator,
  THeadingElement,
} from 'telia-front-react';
import Config from '../Config';
import LanguageStore from '../stores/LanguageStore';
import { TranslationsContext } from '@teliaee/telia-translate';
import StartingCompanyFormModal from './StartingCompanyFormModal';
import StartingCompanySelectPackageCard from './StartingCompanySelectPackageCard';

export interface StartingCompanyPackage {
  title: string;
  icon: string;
  price: number;
  value: string;
  offer?: StartingCompanyPackageOffer;
}

interface StartingCompanyPackageOffer {
  text: string;
  tag: string;
}

export interface StartingCompanyProps {
  packages: StartingCompanyPackage[];
  formName: string;
  packagesTechnicalParameter: string;
  termsLinks?: string;
  headingLevel?: number;
  headingCssClass?: THeadingElement;
  languageStore?: LanguageStore;
}

const StartingCompanyCalculator: React.FC<StartingCompanyProps> = (props) => {
  const [selectedPackages, setSelectedPackages] = React.useState<string[]>([]);
  const [modalOpen, setModalOpen] = React.useState(false);
  const translate = React.useContext(TranslationsContext).translate;
  const { languageStore } = props;

  const toggleSelectedPackages = (pck: string) => {
    if (selectedPackages.includes(pck)) {
      setSelectedPackages(selectedPackages.slice().filter((sp) => sp !== pck));
    } else {
      setSelectedPackages(selectedPackages.slice().concat(pck));
    }
  };

  const getSelectedPackages = () => {
    return props.packages.filter((p) => selectedPackages.includes(p.value));
  };

  return (
    <Container>
      <Card padding="medium">
        <CardContent>
          <Heading className={`text-center ${props.headingCssClass || 'h2'}`} level={props.headingLevel || 2}>
            {translate('starting-company.title')}
          </Heading>
          <Separator color="invisible" spacing="xxsmall" />
          <p className="text-center">{translate('starting-company.sub-title')}</p>
          <Separator color="invisible" spacing="xxsmall" />
          <EqualHeightProvider groups={['card-text']}>
            <Carousel
              fade={false}
              hasContainer={true}
              responsive={[
                {
                  breakPoint: 'lg',
                  props: { slidesToShow: 4 },
                },
                {
                  breakPoint: 'md',
                  props: { slidesToShow: 3 },
                },
              ]}
              breakPoint="md"
              nextArrowText={translate('carousel.next')}
              prevArrowText={translate('carousel.previous')}
            >
              {props.packages.map((card) => (
                <CarouselItem key={card.value}>
                  <StartingCompanySelectPackageCard checkedPackages={selectedPackages} onChange={toggleSelectedPackages} card={card} />
                </CarouselItem>
              ))}
            </Carousel>
          </EqualHeightProvider>
          <Separator color="invisible" spacing="xxsmall" />
          <div className="text-left">
            <AnalyticsTrigger
              trigger={{
                id: 'Link - Kampaania Tingimused',
                callback: 'onClick',
              }}
            >
              <Button
                url={
                  props.termsLinks ||
                  `${Config.teliaWebUrl}/images/documents/ari/alustavettevotja/telia_kampaania_alustavatele_ettevotetele${
                    languageStore.currentLanguage === 'ru' ? '_ru' : ''
                  }.pdf`
                }
                text={translate('starting-company.conditions')}
                iconLeft="#file-pdf"
                link={true}
                target="_blank"
              />
            </AnalyticsTrigger>
          </div>
          <StartingCompanyFormModal
            setModalOpen={setModalOpen}
            open={modalOpen}
            packages={getSelectedPackages()}
            formName={props.formName}
            packagesTechnicalParameter={props.packagesTechnicalParameter}
          />
        </CardContent>
        <CardFooter className="text-right">
          <AnalyticsTrigger trigger={{ id: 'CTA - Form', callback: 'onClick' }}>
            <Button
              disabled={!selectedPackages.length}
              type="expressive"
              text={translate('starting-company.continue')}
              onClick={() => setModalOpen(true)}
              iconRight="#arrow-right"
            />
          </AnalyticsTrigger>
        </CardFooter>
      </Card>
    </Container>
  );
};

export default inject('languageStore')(observer(StartingCompanyCalculator));
